import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useHistoryDemographics } from '@hooks/register/history/use-history-demographics';
import { useLocalize } from '@hooks/use-localize';
import { useScrollToHash } from '@hooks/use-scroll-to-hash';
import { useStepForm } from '@hooks/use-step-form';
import { asString, asStringBool } from '@/utils/parse';
import { createHistoryDemographicsSchema } from '@views/register/history/history-demographics.schema';
import { DashRegistrationStep, historyLinkMap } from '@/types/dashboard';
import type { IHistoryDemographicsFormValues, IHistoryDemographicsModel, IHistoryDemographicsSubmitResult } from '@/types/history/history-demographics';
import EthnicitySection from '@views/register/history/demographic-sections/ethnicity-section';
import IdentificationSection from '@views/register/history/demographic-sections/identification-section';
import ContactSection from '@views/register/history/demographic-sections/contact-section';
import EmploymentSection from '@views/register/history/demographic-sections/employment-section';
import InsurancePhysicianSection from '@views/register/history/demographic-sections/insurance-physician-section';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';

const HistoryDemographics = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useHistoryDemographics(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <HistoryDemographicsContent model={model} submit={submit} isSubmitting={isSubmitting} submitError={submitError} />
        </PageLoader>
    );
};

interface IHistoryDemographicsContentProps {
    model: IHistoryDemographicsModel;
    submit: (values: IHistoryDemographicsFormValues) => Promise<IHistoryDemographicsSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
}

const HistoryDemographicsContent = ({ model, submit, isSubmitting, submitError }: IHistoryDemographicsContentProps) => {
    const t = useLocalize(model.languageData);

    useScrollToHash();

    const form = useStepForm<IHistoryDemographicsFormValues, IHistoryDemographicsSubmitResult>({
        resolver: zodResolver(createHistoryDemographicsSchema(model)),
        defaultValues: {
            ethnicity: asString(model.ethnicity),

            identificationTab: model.identificationTab,
            ssn: model.ssn,
            dlIssueCountry: asString(model.dlIssueCountry),
            dlIssueState: asString(model.dlIssueState),
            dlNonUsStateProvince: model.dlNonUsStateProvince,
            dlNumber: model.dlNumber,
            mothersMaiden: model.mothersMaiden,

            emergencyContact: model.emergencyContact,
            emergencyContactPhone: model.emergencyContactPhone,
            emergencyContactRelationship: asString(model.emergencyContactRelationship),

            isCurrentlyEmployed: asStringBool(model.isCurrentlyEmployed),
            occupation: model.occupation,
            occupationHasHeavyLifting: asStringBool(model.occupationHasHeavyLifting),
            medicalPto: asString(model.medicalPto),
            wageReimbursementImportance: asString(model.wageReimbursementImportance),
            annualIncomeRange: asString(model.annualIncomeRange),
            wantsWageReimbursement: asStringBool(model.wantsWageReimbursement),

            hasHealthInsurance: asStringBool(model.hasHealthInsurance),
            hasPrimaryCarePhysician: asStringBool(model.hasPrimaryCarePhysician),
            physicianName: model.physicianName,

            usCountryValue: model.usCountryValue,
        },
        submit,
        linkMap: historyLinkMap,
    });

    return (
        <StepFormLayout
            kind="history"
            model={model}
            step={DashRegistrationStep.HistoryDemographics}
            title={t('DashLabel_DemographicsPersonal Details', 'Demographics/Personal Details')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div id="ethnicity">
                <EthnicitySection model={model} t={t} />
            </div>

            {model.askForId && (
                <div id="identification">
                    <IdentificationSection model={model} t={t} />
                </div>
            )}

            <div id="contact">
                <ContactSection model={model} t={t} />
            </div>

            <div id="employment">
                <EmploymentSection model={model} t={t} />
            </div>

            <div id="insurance-physician">
                <InsurancePhysicianSection t={t} />
            </div>
        </StepFormLayout>
    );
};

export default HistoryDemographics;
